<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { DashboardStation } from '@/types/station'
import { useI18n } from '@/i18n'

const props = defineProps<{
  station: DashboardStation
}>()
const { locale, t } = useI18n()
const isOffline = computed(() => props.station.status === 'offline')
const hasLastKnownIssue = computed(() =>
  isOffline.value && ['warning', 'alert'].includes(props.station.last_known_status)
)

// Power bar percentage (max at 100kW for visual)
const powerBarPercent = computed(() => {
  const power = props.station.power_kw || 0
  return Math.min((power / 100) * 100, 100)
})

const diagnosticConfig = computed(() => {
  const configs = {
    CAPABLE: { label: t('Diagnostics capable'), className: 'text-emerald-600 bg-emerald-50' },
    DISABLED: { label: t('Diagnostics disabled'), className: 'text-gray-500 bg-gray-50' },
    SHADOW: { label: t('Diagnostics shadow'), className: 'text-blue-600 bg-blue-50' },
    TOPOLOGY_ERROR: { label: t('Topology error'), className: 'text-red-600 bg-red-50' },
    UNAVAILABLE: { label: t('Diagnostics unavailable'), className: 'text-amber-600 bg-amber-50' },
  }
  return configs[props.station.diagnostic_status] || configs.UNAVAILABLE
})

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const dateLocale = locale.value === 'th' ? 'th-TH' : 'en-GB'
  return date.toLocaleDateString(dateLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }) + ' ' + date.toLocaleTimeString(dateLocale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatNumber = (num: number | undefined, decimals: number = 2) => {
  if (num === undefined || num === null) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
</script>

<template>
  <RouterLink
    :to="`/station/${station.station_id}`"
    class="station-card block"
    :class="{ 'station-card--offline': isOffline }"
  >
    <!-- Header -->
    <div class="card-header">
      <div class="flex justify-between items-start">
        <div class="flex items-center space-x-2 min-w-0 flex-1">
          <svg class="w-5 h-5 flex-shrink-0" :class="isOffline ? 'text-gray-400' : 'text-blue-500'" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
          </svg>
          <h3 class="font-semibold text-gray-800 truncate" :title="station.station_name">
            {{ station.station_name }}
          </h3>
        </div>
        <span
          class="status-badge flex-shrink-0 ml-2"
          :class="station.status"
        >
          <svg v-if="station.status === 'active'" class="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="station.status === 'warning'" class="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="station.status === 'alert'" class="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ t(station.status?.toUpperCase() || 'ACTIVE') }}
        </span>
      </div>
    </div>

    <!-- Metrics -->
    <div class="card-body space-y-1">
      <div v-if="isOffline" class="mb-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600">
        <p class="font-medium">{{ t('No current readings') }}</p>
        <p class="mt-0.5 text-xs">{{ t('Last report: {time}', { time: formatDateTime(station.last_reading) }) }}</p>
      </div>

      <!-- Power with color coding -->
      <div class="metric-row">
        <div class="metric-label">
          <span class="metric-icon text-blue-500">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd" />
            </svg>
          </span>
          Power:
        </div>
        <span v-if="isOffline" class="font-semibold text-gray-400">—</span>
        <span v-else class="font-semibold text-blue-600">{{ formatNumber(station.power_kw) }} kW</span>
      </div>
      <!-- Power Bar -->
      <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-300"
          :class="isOffline ? 'bg-gray-300' : 'bg-blue-500'"
          :style="{ width: (isOffline ? 0 : powerBarPercent) + '%' }"
        ></div>
      </div>

      <!-- Voltage P-N -->
      <div class="metric-row">
        <div class="metric-label">
          <span class="metric-icon">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
            </svg>
          </span>
          Avg Voltage (P-N):
        </div>
        <span :class="isOffline ? 'font-semibold text-gray-400' : 'metric-value'">{{ isOffline ? '—' : `${formatNumber(station.voltage_pn)} V` }}</span>
      </div>

      <!-- Voltage L-L -->
      <div class="metric-row">
        <div class="metric-label">
          <span class="metric-icon">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clip-rule="evenodd" />
            </svg>
          </span>
          Avg Voltage (L-L):
        </div>
        <span :class="isOffline ? 'font-semibold text-gray-400' : 'metric-value'">{{ isOffline ? '—' : `${formatNumber(station.voltage_ll)} V` }}</span>
      </div>

      <!-- Current -->
      <div class="metric-row">
        <div class="metric-label">
          <span class="metric-icon">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd" />
            </svg>
          </span>
          Avg Current:
        </div>
        <span :class="isOffline ? 'font-semibold text-gray-400' : 'metric-value'">{{ isOffline ? '—' : `${formatNumber(station.current_avg)} A` }}</span>
      </div>

      <!-- Energy Today (kWh ปัจจุบัน - kWh เที่ยงคืน) -->
      <div class="metric-row">
        <div class="metric-label">
          <span class="metric-icon">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd" />
            </svg>
          </span>
          Energy Today:
        </div>
        <span v-if="isOffline" class="font-semibold text-gray-400">—</span>
        <span v-else class="metric-value">{{ formatNumber(station.energy_kwh) }} kWh</span>
      </div>

      <!-- Last Update -->
      <div class="metric-row">
        <div class="metric-label">
          <span class="metric-icon">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
            </svg>
          </span>
          Last Update:
        </div>
        <span class="text-sm text-gray-600">{{ formatDateTime(station.last_reading) }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="card-footer">
      <div class="flex flex-wrap items-center gap-2">
      <div class="inline-flex items-center px-3 py-1.5 border border-gray-200 rounded-md text-sm text-gray-600 bg-white">
        <svg class="w-4 h-4 mr-1.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13 7H7v6h6V7z" />
          <path fill-rule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clip-rule="evenodd" />
        </svg>
          {{ t((station.meter_count || 0) === 1 ? '{count} meter' : '{count} meters', { count: station.meter_count || 0 }) }}
      </div>
      <div
        class="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium"
        :class="diagnosticConfig.className"
        :title="station.diagnostic_topology_error || diagnosticConfig.label"
      >
        {{ diagnosticConfig.label }}
      </div>
      <div
        v-if="hasLastKnownIssue"
        class="inline-flex items-center px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-500"
      >
        {{ t('Last known status: {status}', { status: t(station.last_known_status.toUpperCase()) }) }}
      </div>
      </div>
    </div>
  </RouterLink>
</template>
